import _ from "lodash";
import {
    IReportEntityType,
    ITenantConfiguration,
    reportEntityTypes,
    tenantConfiguration,
    unsummarizableGoals,
} from "shared/constants/reportConstants";
import {
    IEntitiyTypeEntities,
    IMineTenantElements,
    IReport,
    IReportElement,
    IReportEntity,
    ITenantTableColumn,
} from "shared/reports/models/models";
import { UnitType } from "shared/reports/models/UnityType";
import {
    IScorecardGoalV3_Public,
    IScorecardV3Report_Public,
    IScorecardV3ReportEntity_Public,
    ScorecardGoalV3_Public,
    ScorecardGoalV3_PublicStatus,
    ScorecardGoalV3_PublicTenant,
    ScorecardGoalV3_PublicUnit,
    ScorecardV3Report_PublicPeriodType,
    ScorecardV3ReportEntity_PublicScoreCardType,
} from "shared/service/api/generated";
import { isNumber } from "utils/numberUtils";

export interface GoalAccumulator {
    currentValue: number;
    targetValue: number;
    ahead: number;
}

export interface ITenantGoalsSummary {
    tenant: ITenantConfiguration;
    goalSummary: ScorecardGoal[];
}

export interface IEntityTypeGoalsSummary {
    hasGoalsOtherThanMine: boolean;
    entityType: IReportEntityType;
    tenantGoalsSummaryList: ITenantGoalsSummary[];
}

export class ScorecardGoal extends ScorecardGoalV3_Public implements IReportElement {
    columnKey: string;
    isSummarizable: boolean;
    unitType: UnitType;

    constructor(data?: IScorecardGoalV3_Public) {
        super(data);
        this.columnKey = `${this.tenant}__${this.title}`;
        this.isSummarizable = data?.title && unsummarizableGoals.has(data?.title) ? false : true;

        this.unitType =
            data?.unit === undefined || data?.unit === ScorecardGoalV3_PublicUnit.STRING
                ? UnitType.STRING
                : data?.unit === ScorecardGoalV3_PublicUnit.CURRENCY
                ? UnitType.CURRENCY
                : data.unit === ScorecardGoalV3_PublicUnit.NUMERIC
                ? UnitType.NUMERIC
                : data?.unit === ScorecardGoalV3_PublicUnit.PERCENTAGE
                ? UnitType.PERCENTAGE
                : UnitType.STRING;
    }

    public get sortValue() {
        return this.currentValue;
    }
}

export class ScorecardReportEntity implements IReportEntity {
    scoreCardType?: ScorecardV3ReportEntity_PublicScoreCardType;
    scoreCardTypeAsText?: string;
    userFullName?: string;
    branchName?: string;
    branchCode?: string;
    mine?: boolean;
    username?: string;
    goalList?: ScorecardGoal[];

    constructor(data?: IScorecardV3ReportEntity_Public) {
        this.scoreCardType = data?.scoreCardType;
        this.scoreCardTypeAsText =
            data?.scoreCardType !== undefined
                ? ScorecardV3ReportEntity_PublicScoreCardType[data.scoreCardType]
                : undefined;
        this.userFullName = data?.userFullName;
        this.branchCode = data?.branchCode;
        this.branchName = data?.branchName;
        this.mine = data?.mine;
        this.username = data?.username;
        this.goalList = data?.goalList?.map((i) => new ScorecardGoal(i));
    }

    public get elements() {
        return this.goalList;
    }
}

export class ScorecardReport implements IReport {
    token?: string;
    periodType?: ScorecardV3Report_PublicPeriodType;
    startDate?: Date;
    endDate?: Date;
    description?: string;
    mineElementsByTenant: IMineTenantElements[];
    list?: ScorecardReportEntity[];
    entitiesByType: IEntitiyTypeEntities<ScorecardReportEntity>[];
    entityGoalsSummaryList: IEntityTypeGoalsSummary[];

    constructor(data?: IScorecardV3Report_Public) {
        const entities = data?.list?.map((i) => new ScorecardReportEntity(i));

        this.token = data?.token;
        this.periodType = data?.periodType;
        this.startDate = data?.startDate;
        this.endDate = data?.endDate;
        this.description = data?.description;
        this.list = entities;
        this.mineElementsByTenant = this.getMineReportsByTenant(entities);
        this.entitiesByType = this.getEntitiesByType(entities);
        this.entityGoalsSummaryList = this.getGoalsSummary(entities);
    }

    public get entityTypeList(): IReportEntityType[] {
        return this.entitiesByType.map((i) => i.entityType);
    }

    private getMineReportsByTenant(entities: ScorecardReportEntity[] | undefined): IMineTenantElements[] {
        const result: IMineTenantElements[] = [];

        const mineElements = entities?.find((i) => i.mine === true)?.goalList;

        tenantConfiguration.forEach((tenantConfiguration) => {
            const elementsByTenant = _.sortBy(
                mineElements?.filter((i) => i.tenant === tenantConfiguration.code),
                (i) => i.orderIndex
            );

            if (!elementsByTenant || elementsByTenant.length === 0) {
                return;
            }

            result.push({
                tenant: tenantConfiguration,
                elements: elementsByTenant,
            });
        });

        return result;
    }

    private getEntitiesByType(
        entities: ScorecardReportEntity[] | undefined
    ): IEntitiyTypeEntities<ScorecardReportEntity>[] {
        const result: IEntitiyTypeEntities<ScorecardReportEntity>[] = [];

        reportEntityTypes.forEach((entityType) => {
            const entitiesByType = entities?.filter(
                (i) =>
                    i.scoreCardType !== undefined &&
                    ScorecardV3ReportEntity_PublicScoreCardType[i.scoreCardType] === entityType.code
            );

            if (entitiesByType === undefined || entitiesByType.length === 0) {
                return;
            }

            result.push({
                entityType: entityType,
                entities: entitiesByType,
                tenantTableColumns: this.getTenantTableColumns(entitiesByType[0].goalList),
            });
        });

        return result;
    }

    private getTenantTableColumns(elements: ScorecardGoal[] | undefined): ITenantTableColumn[] {
        const result: ITenantTableColumn[] = [];

        tenantConfiguration.forEach((tenantConfiguration) => {
            const elementsByTenant = _.sortBy(
                _.uniqBy(
                    elements?.filter((i) => i.tenant === tenantConfiguration.code),
                    (i) => i.columnKey
                ),
                (i) => i.orderIndex
            );

            if (!elementsByTenant || elementsByTenant.length === 0) {
                return;
            }

            const currentTenantTableColumns: ITenantTableColumn = {
                tenant: tenantConfiguration,
                tableColumns: [],
            };

            elementsByTenant.forEach((reportElement) => {
                currentTenantTableColumns.tableColumns.push({
                    columnKey: reportElement.columnKey,
                    displayName: reportElement.text ?? reportElement.title ?? "",
                });
            });

            result.push(currentTenantTableColumns);
        });

        return result;
    }

    private getGoalsSummary(entities: ScorecardReportEntity[] | undefined): IEntityTypeGoalsSummary[] {
        const entityGoalsSummaryList: IEntityTypeGoalsSummary[] = [];

        if (entities === undefined || entities.length === 0) {
            return entityGoalsSummaryList;
        }

        reportEntityTypes.forEach((entityType) => {
            const entitiesByType = entities.filter(
                (i) =>
                    i.scoreCardType !== undefined &&
                    ScorecardV3ReportEntity_PublicScoreCardType[i.scoreCardType] === entityType.code
            );

            if (entitiesByType.length === 0) {
                return;
            }

            const currentEntityGoalsSummary: IEntityTypeGoalsSummary = {
                hasGoalsOtherThanMine: entitiesByType.findIndex((i) => i.mine !== true) >= 0,
                entityType: entityType,
                tenantGoalsSummaryList: [],
            };

            tenantConfiguration.forEach((tenantConfiguration) => {
                const tenantGoalsSummary: ITenantGoalsSummary = {
                    tenant: tenantConfiguration,
                    goalSummary: [],
                };

                const firstOfTypeGoalList = entitiesByType
                    ?.find((i) => true)
                    ?.goalList?.filter(
                        (g) =>
                            g.isSummarizable &&
                            g.tenant !== undefined &&
                            ScorecardGoalV3_PublicTenant[g.tenant] === tenantConfiguration.code
                    );

                firstOfTypeGoalList?.forEach((goal) => {
                    const goalsToSum = entitiesByType.flatMap((i) =>
                        i.goalList?.find((i) => i.columnKey === goal.columnKey)
                    );

                    const accumulatedValues = goalsToSum.reduce(
                        (acc, item) => {
                            if (isNumber(item?.currentValue) && isNumber(item?.targetValue) && isNumber(item?.ahead)) {
                                acc.currentValue += item?.currentValue ?? 0;
                                acc.targetValue += item?.targetValue ?? 0;
                                acc.ahead += item?.ahead ?? 0;
                            }
                            return acc;
                        },
                        {
                            currentValue: 0,
                            targetValue: 0,
                            ahead: 0,
                        } as GoalAccumulator
                    );

                    const progressValue =
                        accumulatedValues.targetValue === 0
                            ? undefined
                            : (accumulatedValues.currentValue * 100) / accumulatedValues.targetValue;

                    const accumulatedGoal = new ScorecardGoal({
                        ahead: accumulatedValues.ahead,
                        currentValue: accumulatedValues.currentValue,
                        orderIndex: goal.orderIndex,
                        targetValue: accumulatedValues.targetValue,
                        tenant: goal.tenant,
                        text: goal.text,
                        title: goal.title,
                        progressValue: progressValue,
                        progress: progressValue === undefined ? undefined : Math.min(100, progressValue),
                        status:
                            accumulatedValues.ahead >= 0
                                ? ScorecardGoalV3_PublicStatus.OK
                                : ScorecardGoalV3_PublicStatus.NOK,
                        unit: goal.unit,
                    } as IScorecardGoalV3_Public);

                    tenantGoalsSummary.goalSummary.push(accumulatedGoal);
                });
                tenantGoalsSummary.goalSummary = _.sortBy(tenantGoalsSummary.goalSummary, (i) => i.orderIndex);
                currentEntityGoalsSummary.tenantGoalsSummaryList.push(tenantGoalsSummary);
            });
            entityGoalsSummaryList.push(currentEntityGoalsSummary);
        });
        return entityGoalsSummaryList;
    }
}
